import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  Modal,
  ActionBar,
  ButtonGroup,
  Icon,
  Loader,
  Input,
} from 'shared-components';
import { get, put } from 'shared-components/src/utils/http';
import { isExemptBin } from 'shared-components/src/utils/binExemption';
import { digitsOnly } from '../../../../utils/cardFormatting';
import {
  CONTACT_COUNTRY,
  contactCardErrors,
  etransferContactErrors,
  formatPostalCode,
  toProvinceKey,
} from '../../../../utils/contactCard';
import {
  CardNumberInput,
  PostalCodeInput,
  ProvinceSelect,
  CountrySelect,
  ContactFieldError,
} from '../ContactFields';
import useFieldErrors from '../ContactFields/useFieldErrors';
import { StyledLabel, ModalFormContainer, InputRow, InputItem, CollapsibleSection, InfoRow } from './styles';

import SubmitButton from '../../../../components/SubmitButton';
import CancelButton from '../../../../components/CancelButton';
import Alert from '../../../../components/Alert';

const EditExternalAccountModal = ({
  toggleModal,
  setSuccess,
  onAccountUpdated,
  id,
}) => {
  const intl = useIntl();
  // E-transfer refs
  const contactExternalAccountName = useRef(null);
  const contactInputFirstName = useRef(null);
  const contactInputLastName = useRef(null);
  const contactInputEmail = useRef(null);
  const contactConfirmInputEmail = useRef(null);

  // Debit card formData state (controlled inputs)
  const [formData, setFormData] = useState({
    name: '',
    fullName: '',
    cardNumber: '',
    expiry: '',
    cvv: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
  });

  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const [lastFourDigits, setLastFourDigits] = useState('****');

  const page = 'external-accounts';

  const isBinExempt = isExemptBin(formData.cardNumber);
  const cardNumberLength = digitsOnly(formData.cardNumber).length;
  // Check if account type is e-transfer or debit card
  const isEtransferType =
    accountData?.type === 'external_funding_account' ||
    accountData?.type === 'external_source_account';
  const isDebitCardType =
    accountData?.type === 'personal_funding_account' ||
    accountData?.type === 'personal_source_account';
  const direction = accountData?.type === 'personal_funding_account' ? 'push' : 'pull';

  // e-Transfer fields are uncontrolled (refs); debit card fields live in formData.
  const { fieldErrors, fieldChanged, validateField, validateAll, clearField, invalid } = useFieldErrors(() =>
    (isEtransferType
      ? etransferContactErrors({
        name: contactExternalAccountName.current?.value,
        firstName: contactInputFirstName.current?.value,
        lastName: contactInputLastName.current?.value,
        email: contactInputEmail.current?.value,
        confirmEmail: contactConfirmInputEmail.current?.value,
      })
      : contactCardErrors(formData))
  );

  // Fetch account details on mount
  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const response = await get(
          `/api/v2/cardholder_portal/cardholder/external_accounts/${id}/details`
        );
        setAccountData(response);

        // Populate debit card form data if card exists
        if (response?.card) {
          setLastFourDigits(response.card.last_four_digits || '****');
          setFormData({
            name: response.name || '',
            fullName: response.card.name || '',
            cardNumber: '',
            expiry:
              response.card.expiry_month && response.card.expiry_year
                ? `${response.card.expiry_month}/${response.card.expiry_year}`
                : '',
            cvv: '',
            addressLineOne: response.card.address_line1 || '',
            addressLineTwo: response.card.address_line2 || '',
            city: response.card.address_city || '',
            // Stored values open in the form the fields use ("on" -> "ON", "k1a0b1" -> "K1A 0B1").
            state: toProvinceKey(response.card.address_state),
            country: CONTACT_COUNTRY,
            postalCode: formatPostalCode(response.card.address_postal_code),
          });
        }
      } catch (error) {
        setAlert({
          type: 'danger',
          message:
            error.message ||
            intl.messages[`${page}-fetch-error`] ||
            'Failed to load account details',
        });
      }
      setLoading(false);
    };

    fetchAccountDetails();
  }, [id, intl.messages]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const updateAccount = async () => {
    // Build payload based on account type
    if (isEtransferType) {
      const name = contactExternalAccountName.current.value.trim();
      const firstName = contactInputFirstName.current.value.trim();
      const lastName = contactInputLastName.current.value.trim();
      const email = contactInputEmail.current.value.trim();
      const confirmEmail = contactConfirmInputEmail.current.value.trim();

      if (email !== confirmEmail) {
        throw new Error(
          intl.messages[`${page}-emails-mismatch-error`] ||
            'Emails do not match.'
        );
      }

      const externalAccountPayload = {
        name,
        etransfer: {
          name: `${firstName} ${lastName}`,
          email,
        },
      };

      try {
        await put(
          `/api/v2/cardholder_portal/cardholder/external_accounts/${id}`,
          externalAccountPayload
        );

        const updatedAccount = await get(
          `/api/v2/cardholder_portal/cardholder/external_accounts/${id}/details`
        );

        return updatedAccount;
      } catch (error) {
        throw new Error(
          error.message ||
            intl.messages[`${page}-update-failed-error`] ||
            'Failed to update external account'
        );
      }
    } else if (isDebitCardType) {
      const {
        name,
        fullName,
        cardNumber,
        expiry,
        cvv,
        addressLineOne,
        addressLineTwo,
        city,
        state,
        country,
        postalCode,
      } = formData;

      const cardPayload = {
        name: fullName,
        number: digitsOnly(cardNumber),
        address_line1: addressLineOne,
        address_line2: addressLineTwo,
        address_city: city,
        address_state: state,
        address_country: country,
        address_postal_code: formatPostalCode(postalCode),
      };

      if (expiry && !isBinExempt) {
        const [expiryMonth, expiryYear] = expiry.split('/');
        cardPayload.expiry_month = expiryMonth;
        cardPayload.expiry_year = expiryYear;
      }

      if (cvv && !isBinExempt) {
        cardPayload.cvv = cvv;
      }

      const externalAccountPayload = {
        name,
        card: cardPayload,
      };

      try {
        await put(
          `/api/v2/cardholder_portal/cardholder/external_accounts/${id}`,
          externalAccountPayload
        );

        const updatedAccount = await get(
          `/api/v2/cardholder_portal/cardholder/external_accounts/${id}/details`
        );

        return updatedAccount;
      } catch (error) {
        throw new Error(
          error.message ||
            intl.messages[`${page}-update-failed-error`] ||
            'Failed to update external account'
        );
      }
    }

    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateAll()) return;

    setLoading(true);
    setAlert(null);

    try {
      const updatedAccount = await updateAccount();

      if (onAccountUpdated) {
        onAccountUpdated(updatedAccount);
      }

      setSuccess({
        type: 'success',
        message:
          intl.messages[`${page}-update-success-alert`] ||
          'External account updated successfully',
        messageFor: 'success-update',
        page,
      });

      setLoading(false);
      toggleModal(false);
    } catch (error) {
      setAlert({ type: 'danger', message: error.message });
      setLoading(false);
    }
  };

  // Parse etransfer name into first and last name
  const getFirstName = () => {
    if (!accountData?.etransfer?.name) return '';
    const parts = accountData.etransfer.name.split(' ');
    return parts[0] || '';
  };

  const getLastName = () => {
    if (!accountData?.etransfer?.name) return '';
    const parts = accountData.etransfer.name.split(' ');
    return parts.slice(1).join(' ') || '';
  };

  return (
    <Modal>
      <ModalFormContainer>
        {alert && (
          <Alert
            alert={alert.message}
            alertFor={alert.messageFor}
            page={page}
            type={alert.type}
          />
        )}
        <h2>
          {intl.messages[`${page}-edit-heading`] || 'Edit External Account'}
        </h2>
        {loading ? (
          <Loader />
        ) : (
          // noValidate: every field is checked as it is left and on submit, with its message
          // shown under it, instead of the browser's own bubble.
          <form noValidate onChange={fieldChanged} onSubmit={e => handleSubmit(e)}>
            {/* E-Transfer fields */}
            {isEtransferType && (
              <>
                {/* Account Name for E-Transfer */}
                <StyledLabel htmlFor="contact-name-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-name-input-label`] ||
                    'Account Name'}
                </StyledLabel>
                <Input
                  id="contact-name-input"
                  type="text"
                  ref={contactExternalAccountName}
                  defaultValue={accountData?.name || ''}
                  className={invalid('name')}
                  onBlur={() => validateField('name')}
                  required
                />
                <ContactFieldError error={fieldErrors.name} />
                <StyledLabel htmlFor="contact-first-name-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-first-name-input-label`] ||
                    'First Name'}
                </StyledLabel>
                <Input
                  id="contact-first-name-input"
                  type="text"
                  ref={contactInputFirstName}
                  defaultValue={getFirstName()}
                  className={invalid('firstName')}
                  onBlur={() => validateField('firstName')}
                  required
                />
                <ContactFieldError error={fieldErrors.firstName} />
                <StyledLabel htmlFor="contact-last-name-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-last-name-input-label`] ||
                    'Last Name'}
                </StyledLabel>
                <Input
                  id="contact-last-name-input"
                  type="text"
                  ref={contactInputLastName}
                  defaultValue={getLastName()}
                  className={invalid('lastName')}
                  onBlur={() => validateField('lastName')}
                  required
                />
                <ContactFieldError error={fieldErrors.lastName} />
                <StyledLabel htmlFor="contact-email-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-email-input-label`] || 'Email'}
                </StyledLabel>
                <Input
                  id="contact-email-input"
                  type="email"
                  ref={contactInputEmail}
                  defaultValue={accountData?.etransfer?.email || ''}
                  autoComplete="email"
                  maxLength="254"
                  className={invalid('email')}
                  onBlur={() => validateField('email')}
                  required
                />
                <ContactFieldError error={fieldErrors.email} />
                <StyledLabel htmlFor="contact-confirm-email-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-confirm-email-input-label`] ||
                    'Confirm Email'}
                </StyledLabel>
                <Input
                  id="contact-confirm-email-input"
                  type="email"
                  ref={contactConfirmInputEmail}
                  defaultValue={accountData?.etransfer?.email || ''}
                  autoComplete="email"
                  maxLength="254"
                  className={invalid('confirmEmail')}
                  onBlur={() => validateField('confirmEmail')}
                  required
                />
                <ContactFieldError error={fieldErrors.confirmEmail} />
              </>
            )}

            {/* Debit Card fields */}
            {isDebitCardType && (
              <>
                <InputRow>
                  <InputItem>
                    <StyledLabel htmlFor="contact-name-input-card">
                      <span>*</span>{' '}
                      {intl.messages[`${page}-contact-name-input-label`] ||
                        'Account Name'}
                    </StyledLabel>
                    <Input
                      id="contact-name-input-card"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={invalid('name')}
                      onBlur={() => validateField('name')}
                      required
                    />
                    <ContactFieldError error={fieldErrors.name} />
                  </InputItem>
                  <InputItem>
                    <StyledLabel htmlFor="cardholder-name-input">
                      <span>*</span>{' '}
                      {intl.messages[`${page}-cardholder-name-input-label`] ||
                        'Cardholder Name'}
                    </StyledLabel>
                    <Input
                      id="cardholder-name-input"
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={invalid('fullName')}
                      onBlur={() => validateField('fullName')}
                      required
                    />
                    <ContactFieldError error={fieldErrors.fullName} />
                  </InputItem>
                </InputRow>
                <StyledLabel htmlFor="card-number-input">
                  <span>*</span>{' '}
                  {intl.messages[`contact-fields-card-number-label-${direction}`]}
                </StyledLabel>
                <CardNumberInput
                  id="card-number-input"
                  direction={direction}
                  name="cardNumber"
                  value={formData.cardNumber}
                  onChange={handleChange}
                  placeholder={`**** **** **** ${lastFourDigits}`}
                  className={invalid('cardNumber')}
                  onBlur={() => validateField('cardNumber')}
                  required
                />
                <ContactFieldError error={fieldErrors.cardNumber} />
                {/* CVV + Expiry - hidden by default, show only when card number >= 6 digits and BIN is not exempt */}
                <CollapsibleSection $visible={cardNumberLength >= 6 && !isBinExempt}>
                  <InputRow>
                    <InputItem>
                      <StyledLabel htmlFor="card-expiry-input">
                        <span>*</span>{' '}
                        {intl.messages[`${page}-card-expiry-input-label`] ||
                          'Expiry (MM/YYYY)'}
                      </StyledLabel>
                      <Input
                        id="card-expiry-input"
                        type="text"
                        name="expiry"
                        value={formData.expiry}
                        onChange={handleChange}
                        className={invalid('expiry')}
                        onBlur={() => validateField('expiry')}
                        required={cardNumberLength >= 6 && !isBinExempt}
                      />
                      <ContactFieldError error={fieldErrors.expiry} />
                    </InputItem>
                    <InputItem>
                      <StyledLabel htmlFor="card-cvv-input">
                        <span>*</span>{' '}
                        {intl.messages[`${page}-card-cvv-input-label`] ||
                          'CVV'}
                      </StyledLabel>
                      <Input
                        id="card-cvv-input"
                        type="text"
                        name="cvv"
                        value={formData.cvv}
                        onChange={handleChange}
                        placeholder="***"
                        className={invalid('cvv')}
                        onBlur={() => validateField('cvv')}
                        required={cardNumberLength >= 6 && !isBinExempt}
                      />
                      <ContactFieldError error={fieldErrors.cvv} />
                    </InputItem>
                  </InputRow>
                </CollapsibleSection>
                {/* Info message when CVV/Expiry not needed */}
                <InfoRow $visible={cardNumberLength > 0 && cardNumberLength >= 6 && isBinExempt}>
                  <Icon type="info" size={16} />
                  <span>{intl.messages['external-accounts-card-noCvvNeeded'] || 'No CVV needed.'}</span>
                </InfoRow>
                <StyledLabel htmlFor="address-line1-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-address-line1-input-label`] ||
                    'Address Line 1'}
                </StyledLabel>
                <Input
                  id="address-line1-input"
                  type="text"
                  name="addressLineOne"
                  value={formData.addressLineOne}
                  onChange={handleChange}
                  className={invalid('addressLineOne')}
                  onBlur={() => validateField('addressLineOne')}
                  required
                />
                <ContactFieldError error={fieldErrors.addressLineOne} />
                <StyledLabel htmlFor="address-line2-input">
                  {intl.messages[`${page}-address-line2-input-label`] ||
                    'Address Line 2'}
                </StyledLabel>
                <Input
                  id="address-line2-input"
                  type="text"
                  name="addressLineTwo"
                  value={formData.addressLineTwo}
                  onChange={handleChange}
                />
                <InputRow>
                  <InputItem>
                    <StyledLabel htmlFor="city-input">
                      <span>*</span>{' '}
                      {intl.messages[`${page}-city-input-label`] || 'City'}
                    </StyledLabel>
                    <Input
                      id="city-input"
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={invalid('city')}
                      onBlur={() => validateField('city')}
                      required
                    />
                    <ContactFieldError error={fieldErrors.city} />
                  </InputItem>
                  <InputItem>
                    <StyledLabel htmlFor="state-input">
                      <span>*</span>{' '}
                      {intl.messages[`${page}-state-input-label`] ||
                        'Province/State'}
                    </StyledLabel>
                    <ProvinceSelect
                      id="state-input"
                      value={formData.state}
                      onChange={state => {
                        setFormData(prevState => ({ ...prevState, state }));
                        clearField('state');
                      }}
                      invalid={Boolean(fieldErrors.state)}
                    />
                    <ContactFieldError error={fieldErrors.state} />
                  </InputItem>
                </InputRow>
                <InputRow>
                  <InputItem>
                    <StyledLabel htmlFor="postal-code-input">
                      <span>*</span>{' '}
                      {intl.messages[`${page}-postal-code-input-label`] ||
                        'Postal Code'}
                    </StyledLabel>
                    <PostalCodeInput
                      id="postal-code-input"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      className={invalid('postalCode')}
                      onBlur={() => validateField('postalCode')}
                      required
                    />
                    <ContactFieldError error={fieldErrors.postalCode} />
                  </InputItem>
                  <InputItem>
                    <StyledLabel htmlFor="country-input">
                      <span>*</span>{' '}
                      {intl.messages[`${page}-country-input-label`] || 'Country'}
                    </StyledLabel>
                    <CountrySelect id="country-input" />
                  </InputItem>
                </InputRow>
              </>
            )}
            <ActionBar>
              <ButtonGroup reverse>
                <SubmitButton />
                <CancelButton
                  onClick={e => {
                    e.preventDefault();
                    toggleModal(false);
                  }}
                />
              </ButtonGroup>
            </ActionBar>
          </form>
        )}
      </ModalFormContainer>
    </Modal>
  );
};

EditExternalAccountModal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  setSuccess: PropTypes.func.isRequired,
  onAccountUpdated: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

EditExternalAccountModal.defaultProps = {
  onAccountUpdated: null,
};

export default EditExternalAccountModal;
